import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import matter from 'gray-matter';
import { useThemeContext } from '../context/ThemeContext';
import { Buffer } from 'buffer';

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer;
}

const AnnVisualizer = ({ content }) => {
  const { mode } = useThemeContext();
  const isLight = mode === 'light';

  const { data: metadata } = useMemo(() => {
    try {
      return matter(content);
    } catch {
      return { data: null };
    }
  }, [content]);

  // Helper to safely get numeric coordinates
  const safeCoord = (val, fallback = 0) => {
    const num = Number(val);
    return isNaN(num) ? fallback : num;
  };

  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    setEpoch(0);
  }, [content]);

  if (!metadata || !metadata.epochs) return null;

  const totalEpochs = metadata.epochs;
  const loss = metadata.loss_history[epoch];
  const inputs = metadata.input_vals[epoch];
  const output = metadata.output_vals[epoch];
  const target = metadata.target;

  // Compact Node Coordinates
  const nodes = {
    N1: { x: 150, y: 120, val: inputs[0] },
    N0: { x: 150, y: 230, val: inputs[1] },
    N5: { x: 400, y: 90 },
    N4: { x: 400, y: 150 },
    N3: { x: 400, y: 210 },
    N2: { x: 400, y: 270 },
    N6: { x: 650, y: 175, val: output }
  };

  const inputNodes = ['N1', 'N0'];
  const hiddenNodes = ['N5', 'N4', 'N3', 'N2'];
  const outputNodes = ['N6'];

  return (
    <div
      style={{
        width: '100%',
        margin: '0 auto',
        background: isLight ? '#ffffff' : '#0a0a0c',
        borderRadius: '16px',
        border: isLight ? '1px solid #e2e8f0' : '1px solid #333',
        overflow: 'hidden',
        color: isLight ? '#0f172a' : '#fff',
        boxShadow: isLight ? '0 10px 30px rgba(0,0,0,0.06)' : '0 10px 30px rgba(0,0,0,0.5)'
      }}
    >
      {/* 1. TOP DASHBOARD */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: isLight ? '#f8fafc' : '#18181b',
          padding: '16px 24px',
          borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid #27272a'
        }}
      >
        {/* Left Side: Controls */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => setEpoch(0)}
            style={{
              fontWeight: 'bold',
              borderRadius: '8px',
              backgroundColor: isLight ? '#f1f5f9' : '#27272a',
              color: isLight ? '#475569' : '#fff',
              border: isLight ? '1px solid #cbd5e1' : '1px solid transparent',
              padding: '6px 16px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Reset
          </button>
          <button
            onClick={() => setEpoch(e => e + 1)}
            disabled={epoch >= totalEpochs - 1}
            style={{
              fontWeight: 'bold',
              borderRadius: '8px',
              padding: '6px 24px',
              backgroundColor: epoch >= totalEpochs - 1 ? '#065f46' : '#2563eb',
              color: '#fff',
              border: 'none',
              cursor: epoch >= totalEpochs - 1 ? 'default' : 'pointer',
              fontSize: '13px'
            }}
          >
            {epoch >= totalEpochs - 1 ? 'Training Complete' : 'Next Epoch →'}
          </button>

          <div style={{ marginLeft: '16px' }}>
            <div style={{ fontSize: '10px', color: isLight ? '#64748b' : '#a1a1aa', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1 }}>
              Epoch
            </div>
            <div style={{ fontWeight: 'bold', fontSize: '20px', lineHeight: 1.2 }}>
              {epoch + 1} <span style={{ color: isLight ? '#cbd5e1' : '#52525b' }}>/ {totalEpochs}</span>
            </div>
          </div>
        </div>

        {/* Right Side: Telemetry */}
        <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '10px', color: isLight ? '#64748b' : '#a1a1aa', textTransform: 'uppercase', letterSpacing: '1px' }}>Target</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#34d399', fontFamily: 'monospace' }}>{target.toFixed(2)}</div>
          </div>

          <div style={{ width: '1px', height: '30px', backgroundColor: isLight ? '#e2e8f0' : '#3f3f46' }} />

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '10px', color: isLight ? '#64748b' : '#a1a1aa', textTransform: 'uppercase', letterSpacing: '1px' }}>Model Loss</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', fontFamily: 'monospace', color: loss < 0.2 ? '#34d399' : '#f87171' }}>{loss.toFixed(3)}</div>
          </div>
        </div>
      </div>

      {/* 2. SINGLE FRAME SVG DIAGRAM */}
      <div style={{ width: '100%', padding: '20px', display: 'flex', justifyContent: 'center' }}>
        <svg viewBox="0 0 800 320" style={{ width: '100%', maxHeight: '400px', display: 'block' }}>
          {/* BACKGROUND TEXT LABELS */}
          <g textAnchor="middle" fontFamily="sans-serif">
            <text x="150" y="30" fill={isLight ? '#1e293b' : '#e2e8f0'} fontSize="16" fontWeight="bold">Input Layer</text>
            <text x="150" y="50" fill={isLight ? '#64748b' : '#64748b'} fontSize="11" letterSpacing="1">STANDARDIZED WEIGHTS</text>
            <text x="400" y="30" fill={isLight ? '#1e293b' : '#e2e8f0'} fontSize="16" fontWeight="bold">Hidden Layer</text>
            <text x="400" y="50" fill={isLight ? '#64748b' : '#64748b'} fontSize="11" letterSpacing="1">DECOMPOSITION OF VARIABILITY</text>
            <text x="650" y="30" fill={isLight ? '#1e293b' : '#e2e8f0'} fontSize="16" fontWeight="bold">Output Layer</text>
            <text x="650" y="50" fill={isLight ? '#64748b' : '#64748b'} fontSize="11" letterSpacing="1">PREDICTION</text>
          </g>

          {/* STATIC CONNECTION LINES */}
          <g stroke={isLight ? '#e2e8f0' : '#27272a'} strokeWidth="2">
            {inputNodes.map(inNode =>
              hiddenNodes.map(hidNode => (
                <line 
                  key={`bg-${inNode}-${hidNode}`} 
                  x1={safeCoord(nodes[inNode].x)} y1={safeCoord(nodes[inNode].y)} 
                  x2={safeCoord(nodes[hidNode].x)} y2={safeCoord(nodes[hidNode].y)} 
                />
              ))
            )}
            {hiddenNodes.map(hidNode =>
              outputNodes.map(outNode => (
                <line 
                  key={`bg-${hidNode}-${outNode}`} 
                  x1={safeCoord(nodes[hidNode].x)} y1={safeCoord(nodes[hidNode].y)} 
                  x2={safeCoord(nodes[outNode].x)} y2={safeCoord(nodes[outNode].y)} 
                />
              ))
            )}
          </g>

          {/* ANIMATED PULSES */}
          <AnimatePresence mode="popLayout">
            <g key={`epoch-anim-${epoch}`}>
              {inputNodes.map(inNode =>
                hiddenNodes.map(hidNode => (
                  <motion.line
                    key={`pulse-1-${inNode}-${hidNode}`}
                    x1={safeCoord(nodes[inNode].x)} y1={safeCoord(nodes[inNode].y)} 
                    x2={safeCoord(nodes[hidNode].x)} y2={safeCoord(nodes[hidNode].y)}
                    stroke="#3b82f6" strokeWidth="4" strokeLinecap="round"
                    initial={{ pathLength: 0, opacity: 1 }}
                    animate={{ pathLength: 1, opacity: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                ))
              )}
              {hiddenNodes.map(hidNode =>
                outputNodes.map(outNode => (
                  <motion.line
                    key={`pulse-2-${hidNode}-${outNode}`}
                    x1={safeCoord(nodes[hidNode].x)} y1={safeCoord(nodes[hidNode].y)} 
                    x2={safeCoord(nodes[outNode].x)} y2={safeCoord(nodes[outNode].y)}
                    stroke="#10b981" strokeWidth="4" strokeLinecap="round"
                    initial={{ pathLength: 0, opacity: 1 }}
                    animate={{ pathLength: 1, opacity: 0 }}
                    transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
                  />
                ))
              )}
              {loss > 0.05 && hiddenNodes.map(hidNode => (
                <motion.line
                  key={`backprop-${hidNode}`}
                  x1={safeCoord(nodes.N6.x)} y1={safeCoord(nodes.N6.y)} 
                  x2={safeCoord(nodes[hidNode].x)} y2={safeCoord(nodes[hidNode].y)}
                  stroke="#ef4444" strokeWidth="4" strokeDasharray="8, 8"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: [0, 1, 0] }}
                  transition={{ duration: 0.6, delay: 1.2, ease: "linear" }}
                />
              ))}
            </g>
          </AnimatePresence>

          {/* NODES AND LABELS */}
          {Object.entries(nodes).map(([id, pos]) => {
             const nx = safeCoord(pos.x);
             const ny = safeCoord(pos.y);
             return (
              <g key={id}>
                <circle cx={nx} cy={ny} r="18" fill={id === 'N6' ? '#059669' : '#2563eb'} stroke={isLight ? '#ffffff' : '#0a0a0c'} strokeWidth="4" />
                <text x={nx} y={ny} textAnchor="middle" dy=".3em" fill="#ffffff" fontSize="10" fontWeight="bold" fontFamily="monospace">
                  {id}
                </text>
                {pos.val !== undefined && (
                  <text
                    x={id === 'N6' ? nx + 30 : nx - 30}
                    y={ny}
                    textAnchor={id === 'N6' ? 'start' : 'end'}
                    dy=".3em"
                    fill={id === 'N6' ? (isLight ? '#059669' : '#34d399') : (isLight ? '#475569' : '#94a3b8')}
                    fontSize="16"
                    fontWeight="bold"
                    fontFamily="monospace"
                  >
                    {pos.val.toFixed(2)}
                  </text>
                )}
              </g>
             );
          })}
        </svg>
      </div>
    </div>
  );
};

export default AnnVisualizer;